/**
 * SessionExpiredOverlay -- WP-009 Story 3 (AC-005, AC-006)
 *
 * Renders a non-dismissable full-screen overlay when AuthContext is in
 * unauthenticated state with reason "session_expired". Does NOT render
 * when reason is absent or any other unauthenticated reason value (AC-006).
 *
 * Accessibility:
 * - role="alertdialog" + aria-modal="true" + aria-labelledby + aria-describedby
 * - Focus moves programmatically to "Sign In" button on mount
 * - Tab key is trapped within the overlay (single focusable element)
 * - ESC key is intentionally disabled -- overlay is non-dismissable
 *
 * Animation:
 * - Scrim: sessionScrimIn (opacity 0 -> 1, 200ms)
 * - Card: sessionCardIn (opacity 0 + translateY 12px -> 0, 220ms, 20ms delay)
 * - Both use transform+opacity only (ENGINEERING.md GPU-composited constraint)
 *
 * "Sign In" button:
 * - Min height 48px, full card width (ENGINEERING.md 44px touch target)
 * - Background var(--color-secondary) (interactive blue), white text -- WCAG AA contrast 4.9:1
 * - Calls clearExpiredReason() before navigate so login screen is clean (AC-006)
 */

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { UnlockKeyhole } from './icons';
import { useAuth } from "../context/AuthContext";

export function SessionExpiredOverlay() {
  const { unauthenticatedReason, clearExpiredReason } = useAuth();
  const navigate = useNavigate();
  const signInBtnRef = useRef<HTMLButtonElement>(null);

  const isVisible = unauthenticatedReason === "session_expired";

  // Move focus to Sign In button when overlay appears (AC-005 accessibility)
  useEffect(() => {
    if (isVisible && signInBtnRef.current) {
      signInBtnRef.current.focus();
    }
  }, [isVisible]);

  // Trap focus within overlay -- only one focusable element (AC-005)
  useEffect(() => {
    if (!isVisible) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Tab") {
        // Single focusable element: keep focus on Sign In button
        e.preventDefault();
        signInBtnRef.current?.focus();
      }
      // ESC is intentionally NOT handled -- overlay is non-dismissable
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isVisible]);

  if (!isVisible) return null;

  function handleSignIn() {
    // Reset reason first so login page renders without expiry messaging (AC-006)
    clearExpiredReason();
    navigate("/login");
  }

  return (
    <div
      data-testid="session-expired-scrim"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expired-heading"
      aria-describedby="session-expired-description"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17, 24, 39, 0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 500,
        padding: "var(--space-6)",
        animation: "sessionScrimIn 0.2s ease-out both",
      }}
    >
      <div
        data-testid="session-expired-card"
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-xl)",
          boxShadow: "var(--shadow-modal)",
          padding: "var(--space-8)",
          width: "min(400px, 90vw)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--space-6)",
          textAlign: "center",
          animation: "sessionCardIn 0.22s ease-out 0.02s both",
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "var(--color-error-surface)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <UnlockKeyhole
            size={32}
            color="var(--color-error)"
            aria-hidden="true"
            data-testid="session-expired-icon"
          />
        </div>

        {/* Heading and description */}
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <h2
            id="session-expired-heading"
            data-testid="session-expired-heading"
            style={{
              fontSize: 'var(--fs-xl)',
              fontWeight: 700,
              color: "var(--color-text-primary)",
              lineHeight: "28px",
              margin: 0,
            }}
          >
            You've been signed out.
          </h2>
          <p
            id="session-expired-description"
            data-testid="session-expired-message"
            style={{
              fontSize: 'var(--fs-base)',
              color: "var(--color-text-muted)",
              lineHeight: "22px",
              margin: 0,
            }}
          >
            For your security we sign you out after a while — sign back in to keep going.
          </p>
        </div>

        {/* Sign In button -- min 48px height, full card width (AC-005) */}
        <button
          ref={signInBtnRef}
          data-testid="session-expired-signin-btn"
          type="button"
          onClick={handleSignIn}
          style={{
            width: "100%",
            minHeight: 48,
            fontSize: 'var(--fs-md)',
            fontWeight: 600,
            borderRadius: "var(--radius-md)",
            border: "none",
            background: "var(--color-secondary)",
            color: "var(--color-text-on-primary)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.15s ease",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--color-secondary)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--color-secondary)"; }}
        >
          Sign back in.
        </button>
      </div>
    </div>
  );
}
